import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PaperPlaneTilt, Lightning, Image as ImageIcon, Warning, ShieldWarning, Phone } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

export default function Messages() {
  const { bid } = useParams();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [booking, setBooking] = useState(null);
  const [text, setText] = useState("");
  const [quickActions, setQuickActions] = useState([]);
  const [blocked, setBlocked] = useState(false);
  const scrollRef = useRef(null);

  const load = () => api.get(`/bookings/${bid}/messages`).then((r) => setMessages(r.data || []));

  useEffect(() => {
    api.get("/messages/quick_actions").then((r) => setQuickActions(r.data || []));
    api.get(`/bookings/${bid}`).then((r) => setBooking(r.data)).catch(() => {});
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bid]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    if (!text.trim()) return;
    try {
      await api.post(`/bookings/${bid}/messages`, { kind: "text", body: text });
      setText("");
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Send failed";
      if (msg.includes("blocked")) setBlocked(true);
      toast.error(msg);
    }
  };

  const sendQuick = async (id, meta) => {
    try {
      await api.post(`/bookings/${bid}/messages`, { kind: "quick_action", quick_action: id, quick_meta: meta });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
  };

  const blockOther = async () => {
    if (!booking) return;
    const otherId = user.id === booking.member_id ? booking.professional_id : booking.member_id;
    if (!confirm("Block this user? They won't be able to message you.")) return;
    try {
      await api.post(`/blocks/${otherId}`, { reason: "Blocked from messaging" });
      toast.success("User blocked.");
      setBlocked(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Block failed"); }
  };

  if (!user) return <section className="max-w-md mx-auto px-6 py-24 text-center"><Link to="/auth" className="btn-gold inline-block px-6 py-3 rounded-full text-xs uppercase tracking-[0.25em] font-semibold">Sign in</Link></section>;

  return (
    <div className="max-w-2xl mx-auto px-4 pt-6 pb-32 flex flex-col h-[calc(100vh-64px)]" data-testid="messages-page">
      <div className="mb-3">
        <p className="overline mb-1">Booking-linked chat</p>
        <h1 className="font-display text-2xl text-cream">{booking?.studio_name || "Loading…"}</h1>
        <p className="text-xs text-cream/60">{booking?.service_name} · {booking?.start_time ? new Date(booking.start_time).toLocaleString() : ""}</p>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto border border-cream/10 p-4 space-y-3" data-testid="messages-thread">
        {messages.length === 0 && <p className="text-cream/40 text-xs text-center">No messages yet. Say hi 👋 (privately).</p>}
        {messages.map((m) => {
          const mine = m.sender_id === user.id;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
              <div className={`max-w-[75%] p-3 ${mine ? "bg-gold/10 border border-gold/30 text-cream" : "bg-cream/5 border border-cream/10 text-cream"}`}>
                <p className="text-[10px] uppercase tracking-[0.2em] text-cream/50 mb-1">{m.sender_name} · {m.kind === "quick_action" ? "QUICK" : m.kind}</p>
                <p className="text-sm">{m.body}</p>
                {m.image_url && <img src={m.image_url} alt="ref" className="mt-2 max-w-full max-h-40 border border-cream/10" />}
                <p className="text-[10px] text-cream/40 mt-1">{new Date(m.created_at).toLocaleTimeString()}</p>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-3">
        <p className="overline mb-2 flex items-center gap-2"><Lightning size={12} weight="fill" color="#C5A059" /> Quick actions</p>
        <div className="flex flex-wrap gap-2" data-testid="quick-actions-bar">
          {quickActions.map((q) => (
            <button key={q.id} data-testid={`quick-${q.id}`} onClick={() => sendQuick(q.id, q.id === "running_late" || q.id === "running_behind" ? { minutes: 10 } : null)} className="chip text-[10px] hover:border-gold hover:text-gold">
              {q.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-3 flex gap-2">
        <input
          type="text"
          data-testid="message-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder={blocked ? "Blocked — cannot message" : "Type a message…"}
          disabled={blocked}
          className="flex-1 bg-transparent border border-cream/15 p-3 text-cream focus:border-gold focus:outline-none text-sm"
        />
        <button data-testid="send-btn" onClick={send} disabled={blocked} className="btn-gold px-4 py-2 rounded-full text-[10px] uppercase tracking-[0.2em] font-semibold inline-flex items-center gap-1"><PaperPlaneTilt size={14} /> Send</button>
      </div>

      <div className="mt-4 border-t border-cream/10 pt-3 flex flex-wrap gap-2">
        <button data-testid="msg-call" onClick={() => toast.info("Masked call — placeholder in v1 demo.")} className="btn-ghost px-4 py-2 rounded-full text-[10px] uppercase tracking-[0.2em] inline-flex items-center gap-1"><Phone size={12} /> Call (privacy-protected)</button>
        <Link to={`/safety/${bid}`} data-testid="msg-safety" className="btn-ghost px-4 py-2 rounded-full text-[10px] uppercase tracking-[0.2em] inline-flex items-center gap-1 text-red-300 border-red-300/30"><Warning size={12} /> Safety</Link>
        <button data-testid="msg-block" onClick={blockOther} className="btn-ghost px-4 py-2 rounded-full text-[10px] uppercase tracking-[0.2em] inline-flex items-center gap-1"><ShieldWarning size={12} /> Block</button>
      </div>
    </div>
  );
}
